import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Box,
  Typography,
  TextField,
  Dialog,
  Paper,
  CircularProgress,
} from '@mui/material';
import styles from './Edit1RMDialog.module.scss';

const formatDate = (dateString: string): string => {
  if (!dateString) {
    return '';
  }
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${month}/${day}/${year}`;
};

const TransparentDialogPaper = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof Paper>
>((props, ref) => <Paper {...props} ref={ref} elevation={0} />);
TransparentDialogPaper.displayName = 'TransparentDialogPaper';

interface Edit1RMDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (newValue: number) => void;
  exerciseName: string;
  currentValue: number;
  lastUpdateDate: string;
}

const Edit1RMDialog: React.FC<Edit1RMDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  exerciseName,
  currentValue,
  lastUpdateDate,
}) => {
  const [value, setValue] = useState(String(currentValue));
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(String(currentValue));
  }, [currentValue, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const animationFrame = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(animationFrame);
  }, [isOpen]);

  const handleSave = async () => {
    setLoading(true);
    await onSave(value === '' ? 0 : Number(value));
    setLoading(false);
    onClose();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (/^[0-9]*$/.test(val)) {
      setValue(val);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      PaperComponent={TransparentDialogPaper}
      sx={{
        '& .MuiDialog-paper': {
          backgroundColor: 'transparent',
        },
      }}
    >
      <div className={styles.container}>
        <div>
          <Typography className={styles.title}>Edit Current 1RM</Typography>
          {lastUpdateDate && (
            <Typography className={styles.date}>
              {formatDate(lastUpdateDate)}
            </Typography>
          )}
        </div>
        <Box className={styles.inputContainer}>
          <TextField
            size="small"
            type="text"
            inputMode="numeric"
            value={value}
            onChange={handleChange}
            placeholder="0"
            variant="outlined"
            className={styles.inputField}
            inputRef={inputRef}
            autoFocus
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '15px',
                '& fieldset': {
                  borderColor: 'var(--color-bg-5)',
                },
              },
              '& input::placeholder': {
                color: 'var(--color-text-4)',
                opacity: 1,
              },
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <Typography className={styles.unit}>lbs</Typography>
                ),
              },
            }}
          />
        </Box>
        <Box className={styles.actions}>
          <Button onClick={onClose} className={styles.cancelButton}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className={styles.saveButton}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </Box>
      </div>
    </Dialog>
  );
};

export default Edit1RMDialog;
